// @ts-nocheck
/**
 * DeleteConfirmModal Component
 *
 * Confirmation dialog for deleting a user sticky note.
 * Shows "Are you sure?" message with Cancel and Delete buttons.
 *
 * Features:
 * - Portal rendering for proper z-index
 * - Animated appearance/disappearance
 * - Keyboard support (Escape to cancel)
 * - Focus management
 * - 44px touch targets for mobile
 */

import React, { useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { AlertTriangle } from 'lucide-react';
import { useIsMobile } from '@shared/hooks/useIsMobile';

interface DeleteConfirmModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Animation variants for backdrop
 */
const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.15 } },
  exit: { opacity: 0, transition: { duration: 0.15 } },
};

/**
 * Animation variants for modal
 */
const modalVariants = {
  hidden: { opacity: 0, scale: 0.95, y: -10 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 400, damping: 30 },
  },
  exit: { opacity: 0, scale: 0.95, y: -10, transition: { duration: 0.15 } },
};

/**
 * Reduced motion variants
 */
const reducedMotionBackdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.1 } },
  exit: { opacity: 0, transition: { duration: 0.1 } },
};

const reducedMotionModalVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.1 } },
  exit: { opacity: 0, transition: { duration: 0.1 } },
};

export const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  isOpen,
  onCancel,
  onConfirm,
}) => {
  const isMobile = useIsMobile();
  const prefersReducedMotion = useReducedMotion();
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  const backdropVars = prefersReducedMotion ? reducedMotionBackdropVariants : backdropVariants;
  const modalVars = prefersReducedMotion ? reducedMotionModalVariants : modalVariants;

  // Focus cancel button on open
  useEffect(() => {
    if (isOpen && cancelButtonRef.current) {
      cancelButtonRef.current.focus();
    }
  }, [isOpen]);

  // Handle keyboard events
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    },
    [onCancel]
  );

  // Handle backdrop click
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onCancel();
      }
    },
    [onCancel]
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          variants={backdropVars}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={handleBackdropClick}
          onKeyDown={handleKeyDown}
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-modal-title"
          aria-describedby="delete-modal-description"
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" aria-hidden="true" />

          {/* Modal */}
          <motion.div
            className={`
              relative bg-dark-surface
              rounded-xl shadow-2xl shadow-black/20
              max-w-sm w-full mx-4
              border border-dark-border/30
            `}
            variants={modalVars}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              {/* Icon and Title */}
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-900/30 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-400" aria-hidden="true" />
                </div>
                <div>
                  <h3
                    id="delete-modal-title"
                    className="text-lg font-semibold text-white"
                  >
                    Delete Sticky Note
                  </h3>
                  <p
                    id="delete-modal-description"
                    className="mt-2 text-sm text-dark-muted"
                  >
                    Are you sure you want to delete this sticky note? This action cannot be undone.
                  </p>
                </div>
              </div>

              {/* Buttons */}
              <div className={`mt-6 flex gap-3 ${isMobile ? 'flex-col' : 'flex-row-reverse'}`}>
                <button
                  onClick={onConfirm}
                  className={`
                    flex-1 px-4 py-2 rounded-lg
                    bg-red-600 hover:bg-red-700
                    text-white font-medium text-sm
                    transition-all duration-150
                    focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2
                    ${isMobile ? 'min-h-[44px]' : ''}
                  `}
                  aria-label="Confirm delete"
                  data-testid="confirm-delete-button"
                >
                  Delete
                </button>
                <button
                  ref={cancelButtonRef}
                  onClick={onCancel}
                  className={`
                    flex-1 px-4 py-2 rounded-lg
                    bg-dark-input hover:bg-dark-surface/40
                    text-dark-text font-medium text-sm
                    transition-all duration-150
                    focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2
                    ${isMobile ? 'min-h-[44px]' : ''}
                  `}
                  aria-label="Cancel delete"
                  data-testid="cancel-delete-button"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

